import { Check, ChevronDown, Minus, Plus, RotateCcw, Sparkles, Target, X } from 'lucide-react'
import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export interface AtmRangePickerProps {
    value: number
    onChange: (value: number) => void
    min?: number
    max?: number
    className?: string
}

// Strategic options presets with trading context
const STRATEGIC_PRESETS = [
    { range: 3, label: '±3', subtitle: '7 str', desc: 'Scalp' },
    { range: 5, label: '±5', subtitle: '11 str', desc: 'Tight' },
    { range: 8, label: '±8', subtitle: '17 str', desc: 'Intraday' },
    { range: 10, label: '±10', subtitle: '21 str', desc: 'Standard' },
    { range: 11, label: '±11', subtitle: '23 str', desc: 'Pro Matrix' },
    { range: 15, label: '±15', subtitle: '31 str', desc: 'Wide' },
    { range: 20, label: '±20', subtitle: '41 str', desc: 'Deep' },
    { range: 25, label: '±25', subtitle: '51 str', desc: 'Macro' },
    { range: 30, label: '±30', subtitle: '61 str', desc: 'Extended' },
    { range: 50, label: '±50', subtitle: '101 str', desc: 'Centurion' },
    { range: 75, label: '±75', subtitle: '151 str', desc: 'Broad' },
    { range: 100, label: '±100', subtitle: '201 str', desc: 'Max Depth' },
]

export const AtmRangePicker: React.FC<AtmRangePickerProps> = ({
    value,
    onChange,
    min = 1,
    max = 100,
    className = '',
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [editInputValue, setEditInputValue] = useState(String(value))
    const containerRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})

    // Hold-to-repeat refs for + / - steppers
    const repeatTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const repeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

    const clearRepeat = useCallback(() => {
        if (repeatTimeoutRef.current) {
            clearTimeout(repeatTimeoutRef.current)
            repeatTimeoutRef.current = null
        }
        if (repeatIntervalRef.current) {
            clearInterval(repeatIntervalRef.current)
            repeatIntervalRef.current = null
        }
    }, [])

    useEffect(() => {
        return () => clearRepeat()
    }, [clearRepeat])

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [isEditing])

    // Ensure value is strictly within bounds [min, max]
    const clampedValue = useMemo(() => {
        const num = Number(value)
        if (Number.isNaN(num) || num < min) return min
        if (num > max) return max
        return num
    }, [value, min, max])

    const totalStrikes = 2 * clampedValue + 1

    const handleStep = useCallback(
        (delta: number) => {
            const nextVal = Math.min(max, Math.max(min, clampedValue + delta))
            if (nextVal !== clampedValue) {
                onChange(nextVal)
            }
        },
        [clampedValue, min, max, onChange],
    )

    // Repeat on hold handlers
    const startRepeating = useCallback(
        (delta: number) => {
            handleStep(delta)
            clearRepeat()
            repeatTimeoutRef.current = setTimeout(() => {
                repeatIntervalRef.current = setInterval(() => {
                    handleStep(delta)
                }, 80)
            }, 250)
        },
        [handleStep, clearRepeat],
    )

    // Viewport-aware positioning
    const updatePosition = useCallback(() => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect()
            const dropdownWidth = 360
            const dropdownHeight = 490
            let left = rect.left - 40
            let top = rect.bottom + 10

            // Horizontal bounds check
            if (left + dropdownWidth > window.innerWidth - 12) {
                left = window.innerWidth - dropdownWidth - 12
            }
            if (left < 12) {
                left = 12
            }

            // Vertical bounds check (flip upwards if needed)
            if (top + dropdownHeight > window.innerHeight - 12) {
                if (rect.top - dropdownHeight - 10 > 12) {
                    top = rect.top - dropdownHeight - 10
                } else {
                    top = Math.max(12, window.innerHeight - dropdownHeight - 12)
                }
            }

            setDropdownStyle({
                position: 'fixed',
                top: `${top}px`,
                left: `${left}px`,
                width: `${Math.min(dropdownWidth, window.innerWidth - 24)}px`,
                zIndex: 99999,
            })
        }
    }, [])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                const target = event.target as Element
                if (!target.closest('.atm-range-picker-portal')) {
                    setIsOpen(false)
                    setIsEditing(false)
                }
            }
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (!isOpen) return

            if (event.key === 'Escape') {
                setIsOpen(false)
                setIsEditing(false)
            } else if (!isEditing) {
                if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
                    event.preventDefault()
                    handleStep(-1)
                } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
                    event.preventDefault()
                    handleStep(1)
                }
            }
        }

        const handleUpdate = () => {
            if (isOpen) updatePosition()
        }

        if (isOpen) {
            updatePosition()
            document.addEventListener('mousedown', handleClickOutside)
            document.addEventListener('keydown', handleKeyDown)
            window.addEventListener('scroll', handleUpdate, true)
            window.addEventListener('resize', handleUpdate)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleKeyDown)
            window.removeEventListener('scroll', handleUpdate, true)
            window.removeEventListener('resize', handleUpdate)
        }
    }, [isOpen, isEditing, updatePosition, handleStep])

    const handleCommitDirectInput = () => {
        const parsed = parseInt(editInputValue, 10)
        if (!Number.isNaN(parsed)) {
            const bounded = Math.min(max, Math.max(min, parsed))
            onChange(bounded)
        }
        setIsEditing(false)
    }

    // Dynamic Strike Envelope distribution bar
    // ITM: Green, ATM: Amber/Gold, OTM: Rose
    const strikeVisualWidth = Math.min(100, Math.max(15, (clampedValue / max) * 100))

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            {/* Header Trigger Pill with Inline Steppers */}
            <div className="flex items-center gap-1 lg:gap-1.5 px-2 lg:px-2.5 py-1 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-750 rounded-full border border-slate-200/90 dark:border-slate-700/80 shadow-sm shrink-0 transition-all select-none group">
                {/* Micro Minus Stepper (Direct 1-click nudge) */}
                <button
                    type="button"
                    title="Decrease range by 1"
                    disabled={clampedValue <= min}
                    onClick={(e) => {
                        e.stopPropagation()
                        handleStep(-1)
                    }}
                    className="w-5 h-5 rounded-full flex items-center justify-center text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-slate-700/80 active:scale-90 disabled:opacity-30 disabled:pointer-events-none transition-all cursor-pointer"
                >
                    <Minus className="w-3 h-3 stroke-[2.5]" />
                </button>

                {/* Central Interactive Click Area */}
                <button
                    type="button"
                    onClick={() => {
                        setEditInputValue(String(clampedValue))
                        setIsOpen((prev) => !prev)
                    }}
                    className="flex items-center gap-1.5 outline-none bg-transparent border-none cursor-pointer py-0.5 px-1"
                    title={`ATM ± ${clampedValue} Strikes (${totalStrikes} total strikes). Click to customize.`}
                >
                    <Target className="w-3.5 lg:w-4 h-3.5 lg:h-4 text-indigo-500 dark:text-indigo-400 shrink-0 group-hover:rotate-45 transition-transform duration-300" />
                    <span className="text-[10px] lg:text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 shrink-0">
                        ATM ±
                    </span>
                    <span className="text-[12px] lg:text-[14px] font-black premium-cell-mono text-slate-800 dark:text-slate-100 tabular-nums">
                        {clampedValue}
                    </span>
                    <span className="text-[9px] font-bold px-1.5 py-0.2 rounded-full bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-300 border border-indigo-200/60 dark:border-indigo-800/60 tabular-nums hidden sm:inline-block">
                        {totalStrikes}s
                    </span>
                    <ChevronDown
                        className={`w-3 h-3 text-slate-400 group-hover:text-indigo-500 transition-transform duration-200 ${isOpen ? 'rotate-180 text-indigo-500' : ''}`}
                    />
                </button>

                {/* Micro Plus Stepper (Direct 1-click nudge) */}
                <button
                    type="button"
                    title="Increase range by 1"
                    disabled={clampedValue >= max}
                    onClick={(e) => {
                        e.stopPropagation()
                        handleStep(1)
                    }}
                    className="w-5 h-5 rounded-full flex items-center justify-center text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-slate-700/80 active:scale-90 disabled:opacity-30 disabled:pointer-events-none transition-all cursor-pointer"
                >
                    <Plus className="w-3 h-3 stroke-[2.5]" />
                </button>
            </div>

            {/* Comprehensive Portal Popover */}
            {isOpen &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        className="atm-range-picker-portal bg-white/95 dark:bg-slate-900/95 backdrop-blur-3xl rounded-[24px] shadow-[0_25px_60px_-15px_rgba(79,70,229,0.35)] dark:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] border border-slate-200/90 dark:border-slate-700/80 ring-1 ring-black/5 dark:ring-white/10 overflow-hidden animate-zoom-in duration-200 select-none"
                        style={dropdownStyle}
                    >
                        {/* Ambient Glows */}
                        <div className="absolute top-0 right-0 w-36 h-36 bg-indigo-500/15 blur-[50px] rounded-full pointer-events-none" />
                        <div className="absolute bottom-0 left-0 w-36 h-36 bg-emerald-500/15 blur-[50px] rounded-full pointer-events-none" />

                        <div className="relative p-5">
                            {/* Top Bar: Title & Reset/Close Actions */}
                            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-2">
                                    <div className="w-7 h-7 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center text-indigo-600 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-800/40">
                                        <Target className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <h4 className="text-[12px] font-black tracking-tight text-slate-800 dark:text-slate-100 uppercase">
                                            Strike Range Depth
                                        </h4>
                                        <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500">
                                            ATM ± {clampedValue} Strikes (1 - {max})
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-1.5">
                                    {clampedValue !== 10 && (
                                        <button
                                            type="button"
                                            onClick={() => onChange(10)}
                                            className="px-2 py-0.5 rounded-md text-[10px] font-bold text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-1"
                                            title="Reset to default 10 strikes"
                                        >
                                            <RotateCcw className="w-2.5 h-2.5" />
                                            <span>Reset (10)</span>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setIsOpen(false)}
                                        className="w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Center Hero: Tactile Steppers & Digital Readout */}
                            <div className="py-4 flex flex-col items-center">
                                <div className="flex items-center justify-center gap-4 w-full">
                                    {/* Tactile Large Minus Button */}
                                    <button
                                        type="button"
                                        disabled={clampedValue <= min}
                                        onMouseDown={() => startRepeating(-1)}
                                        onMouseUp={clearRepeat}
                                        onMouseLeave={clearRepeat}
                                        onTouchStart={() => startRepeating(-1)}
                                        onTouchEnd={clearRepeat}
                                        className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 border border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-700 flex items-center justify-center transition-all duration-150 active:scale-95 disabled:opacity-30 disabled:pointer-events-none shadow-sm cursor-pointer"
                                        title="Decrease (hold to scroll fast)"
                                    >
                                        <Minus className="w-5 h-5 stroke-[2.5]" />
                                    </button>

                                    {/* Central Number Display */}
                                    <div className="flex flex-col items-center">
                                        {isEditing ? (
                                            <input
                                                ref={inputRef}
                                                type="number"
                                                min={min}
                                                max={max}
                                                value={editInputValue}
                                                onChange={(e) => setEditInputValue(e.target.value)}
                                                onBlur={handleCommitDirectInput}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleCommitDirectInput()
                                                    if (e.key === 'Escape') setIsEditing(false)
                                                }}
                                                className="w-24 text-center text-4xl font-black font-mono tracking-tight text-indigo-600 dark:text-indigo-400 bg-slate-100 dark:bg-slate-800 rounded-xl p-1 border-2 border-indigo-500 outline-none"
                                            />
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEditInputValue(String(clampedValue))
                                                    setIsEditing(true)
                                                }}
                                                className="flex items-center justify-center gap-1 group/num px-3 py-1 rounded-2xl hover:bg-slate-100/60 dark:hover:bg-slate-800/60 transition-colors"
                                                title="Click or double click to type number directly"
                                            >
                                                <span className="text-2xl font-bold text-slate-400 dark:text-slate-500 group-hover/num:text-indigo-500 transition-colors">
                                                    ±
                                                </span>
                                                <span className="text-4xl font-black font-mono tracking-tight text-slate-900 dark:text-white tabular-nums group-hover/num:text-indigo-600 dark:group-hover/num:text-indigo-400 transition-colors">
                                                    {clampedValue}
                                                </span>
                                            </button>
                                        )}
                                        <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 tracking-wider uppercase mt-0.5">
                                            Strikes from ATM
                                        </span>
                                    </div>

                                    {/* Tactile Large Plus Button */}
                                    <button
                                        type="button"
                                        disabled={clampedValue >= max}
                                        onMouseDown={() => startRepeating(1)}
                                        onMouseUp={clearRepeat}
                                        onMouseLeave={clearRepeat}
                                        onTouchStart={() => startRepeating(1)}
                                        onTouchEnd={clearRepeat}
                                        className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 border border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-700 flex items-center justify-center transition-all duration-150 active:scale-95 disabled:opacity-30 disabled:pointer-events-none shadow-sm cursor-pointer"
                                        title="Increase (hold to scroll fast)"
                                    >
                                        <Plus className="w-5 h-5 stroke-[2.5]" />
                                    </button>
                                </div>

                                {/* Strike Envelope Breakdown Card */}
                                <div className="mt-4 w-full bg-slate-50 dark:bg-slate-800/60 rounded-2xl p-3 border border-slate-200/70 dark:border-slate-700/60 flex flex-col gap-2">
                                    <div className="flex items-center justify-between text-[11px] font-bold">
                                        <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                            ITM: {clampedValue}
                                        </span>
                                        <span className="text-amber-600 dark:text-amber-400 flex items-center gap-1 font-black">
                                            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                                            ATM: 1
                                        </span>
                                        <span className="text-rose-600 dark:text-rose-400 flex items-center gap-1">
                                            <span className="w-2 h-2 rounded-full bg-rose-500" />
                                            OTM: {clampedValue}
                                        </span>
                                    </div>

                                    {/* Visual Proportional Envelope Bar */}
                                    <div className="relative w-full h-3 bg-slate-200/80 dark:bg-slate-700/80 rounded-full overflow-hidden flex items-center">
                                        <div
                                            className="h-full bg-gradient-to-r from-emerald-500 via-emerald-400 to-indigo-500 transition-all duration-150 rounded-l-full"
                                            style={{ width: `${strikeVisualWidth / 2}%` }}
                                        />
                                        <div
                                            className="w-3 h-full bg-amber-400 shrink-0 z-10 shadow-sm"
                                            title="ATM Strike"
                                        />
                                        <div
                                            className="h-full bg-gradient-to-r from-indigo-500 via-rose-400 to-rose-500 transition-all duration-150 rounded-r-full"
                                            style={{ width: `${strikeVisualWidth / 2}%` }}
                                        />
                                    </div>

                                    <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400 font-bold px-0.5">
                                        <span>Formula: 2 × {clampedValue} + 1</span>
                                        <span className="font-black text-indigo-600 dark:text-indigo-400 font-mono">
                                            = {totalStrikes} Total Strikes
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Continuous Smooth Slider */}
                            <div className="py-2 flex flex-col gap-1.5">
                                <div className="flex items-center justify-between text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                    <span>Depth Slider</span>
                                    <span className="text-indigo-600 dark:text-indigo-400 font-mono font-black">
                                        {clampedValue} / {max}
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min={min}
                                    max={max}
                                    step={1}
                                    value={clampedValue}
                                    onChange={(e) => onChange(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-indigo-600 dark:accent-indigo-400 transition-all"
                                />
                                <div className="flex justify-between text-[9px] font-mono text-slate-400 dark:text-slate-500 px-0.5 font-bold">
                                    <span>1</span>
                                    <span>10</span>
                                    <span>25</span>
                                    <span>50</span>
                                    <span>75</span>
                                    <span>100</span>
                                </div>
                            </div>

                            {/* Strategic Quick Presets Grid */}
                            <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500 flex items-center gap-1">
                                        <Sparkles className="w-3 h-3 text-amber-500" />
                                        Strategic Presets
                                    </span>
                                    <span className="text-[9px] text-slate-400">1-Tap Select</span>
                                </div>

                                <div className="grid grid-cols-4 gap-1.5">
                                    {STRATEGIC_PRESETS.map((preset) => {
                                        const isSelected = clampedValue === preset.range
                                        return (
                                            <button
                                                key={preset.range}
                                                type="button"
                                                onClick={() => onChange(preset.range)}
                                                className={`py-1.5 px-1 rounded-xl flex flex-col items-center justify-center transition-all duration-150 relative cursor-pointer border ${
                                                    isSelected
                                                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-500/25 scale-[1.02]'
                                                        : 'bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200/70 dark:border-slate-700/60'
                                                }`}
                                            >
                                                <span
                                                    className={`text-[12px] font-black font-mono leading-tight ${isSelected ? 'text-white' : 'text-slate-800 dark:text-slate-100'}`}
                                                >
                                                    {preset.label}
                                                </span>
                                                <span
                                                    className={`text-[8.5px] font-bold leading-tight ${isSelected ? 'text-indigo-100' : 'text-slate-400 dark:text-slate-500'}`}
                                                >
                                                    {preset.subtitle}
                                                </span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>

                            {/* Bottom Done Action Button */}
                            <div className="pt-4 mt-2">
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="w-full py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-black text-[12px] uppercase tracking-wider shadow-md shadow-indigo-600/30 transition-all active:scale-[0.98] cursor-pointer flex items-center justify-center gap-1.5"
                                >
                                    <Check className="w-3.5 h-3.5 stroke-[3]" />
                                    <span>Apply & Close</span>
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    )
}
